// clientSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/services/api';

// Fetch all clients
export const fetchClients = createAsyncThunk(
    'clients/fetchClients',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/api/clients');
            if (response.data.status === 'error') {
                return rejectWithValue(response.data.message);
            }
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
        }
    }
);

// Fetch projects for a specific client
export const fetchClientProjects = createAsyncThunk(
    'clients/fetchClientProjects',
    async (clientName, { rejectWithValue }) => {
        try {
            if (!clientName) {
                return rejectWithValue('Client name is required');
            }
            const response = await api.get(`/api/clients/${encodeURIComponent(clientName)}/projects`);
            if (response.data.status === 'error') {
                return rejectWithValue(response.data.message);
            }
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch client projects');
        }
    }
);

// Create new client
export const createClient = createAsyncThunk(
    'clients/createClient',
    async (clientData, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/clients/upsert', clientData);
            if (response.data.status === 'error') {
                return rejectWithValue(response.data.message);
            }
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to create client');
        }
    }
);

// Update client
export const updateClient = createAsyncThunk(
    'clients/updateClient',
    async ({ projectCode, clientData }, { rejectWithValue }) => {
        try {
            const response = await api.put(
                `/api/clients/update/${encodeURIComponent(projectCode)}`,
                clientData
            );
            
            if (response.data.status === 'error') {
                return rejectWithValue(response.data.message);
            }
            
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
            return rejectWithValue(
                error.response?.data?.message || 'Failed to update client'
            );
        }
    }
);

// Delete client
export const deleteClient = createAsyncThunk(
    'clients/deleteClient',
    async (project_code, { rejectWithValue }) => {
        try {
            const response = await api.delete(`/api/clients/delete/${encodeURIComponent(project_code)}`);
            if (response.data.status === 'error') {
                return rejectWithValue(response.data.message);
            }
            return { project_code, ...response.data.data };
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to delete client');
        }
    }
);

const clientSlice = createSlice({
    name: 'clients',
    initialState: {
        list: [],
        selectedClientProjects: [],
        selectedClient: null,
        loading: false,
        error: null,
        lastUpdated: null,
        createStatus: 'idle',
        updateStatus: 'idle',
        deleteStatus: 'idle'
    },
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearClientProjects: (state) => {
            state.selectedClientProjects = [];
        },
        clearClients: (state) => {
            state.list = [];
            state.selectedClientProjects = [];
            state.selectedClient = null;
            state.lastUpdated = null;
        },
        setSelectedClient: (state, action) => {
            state.selectedClient = action.payload;
        },
        resetStatus: (state) => {
            state.createStatus = 'idle';
            state.updateStatus = 'idle';
            state.deleteStatus = 'idle';
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Clients
            .addCase(fetchClients.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClients.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch clients';
            })

            // Fetch Client Projects
            .addCase(fetchClientProjects.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClientProjects.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedClientProjects = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchClientProjects.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch client projects';
            })

            // Create Client
            .addCase(createClient.pending, (state) => {
                state.createStatus = 'loading';
                state.error = null;
            })
            .addCase(createClient.fulfilled, (state, action) => {
                state.createStatus = 'succeeded';
                // Don't push to list - will be refreshed on next fetch
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(createClient.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.payload || 'Failed to create client';
            })

            // Update Client
            .addCase(updateClient.pending, (state) => {
                state.updateStatus = 'loading';
                state.error = null;
            })
            .addCase(updateClient.fulfilled, (state, action) => {
                state.updateStatus = 'succeeded';
                // Don't update list - will be refreshed on next fetch
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(updateClient.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.payload || 'Failed to update client';
            })

            // Delete Client
            .addCase(deleteClient.pending, (state) => {
                state.deleteStatus = 'loading';
                state.error = null;
            })
            .addCase(deleteClient.fulfilled, (state, action) => {
                state.deleteStatus = 'succeeded';
                state.list = state.list.filter(
                    client => client.client_name !== action.payload.clientName
                );
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(deleteClient.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.payload || 'Failed to delete client';
            });
    },
});

export const {
    clearError,
    clearClientProjects,
    clearClients,
    setSelectedClient,
    resetStatus
} = clientSlice.actions;

// Selectors
export const selectClients = (state) => state.clients.list;
export const selectSelectedClient = (state) => state.clients.selectedClient;
export const selectSelectedClientProjects = (state) => state.clients.selectedClientProjects;
export const selectClientsLoading = (state) => state.clients.loading;
export const selectClientsError = (state) => state.clients.error;
export const selectLastUpdated = (state) => state.clients.lastUpdated;
export const selectCreateStatus = (state) => state.clients.createStatus;
export const selectUpdateStatus = (state) => state.clients.updateStatus;
export const selectDeleteStatus = (state) => state.clients.deleteStatus;

export default clientSlice.reducer;